/** Publish an app (project managers on Pro and above):
 *  choose app → store page → price → free trial → review → submitted. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  assetUrl, budgetCheckout, budgetFromWallet, createListing, deleteScreenshot, fmtDate, getListing,
  getSellProjects, money, mktError, submitListing, updateListing, uploadScreenshot,
  type Judgement, type Listing, type Pricing, type PublishableProject,
} from '@/api/marketplace'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import MarketplaceShell, { AppTile, Steps } from './MarketplaceShell'

const LABELS = ['Choose app', 'Store page', 'Price', 'Free trial', 'Review']

/** Mirrors services/marketplace/pricing.judge — instant feedback while typing;
 *  the server re-judges on save and submit. */
function judge(price: number | null, cost: number, feePct: number, currency: string): Judgement {
  const p = price || 0
  const fee = Math.round(p * feePct / 100)
  const earn = p - fee - cost
  const breakEven = feePct < 100 ? Math.ceil(cost / (1 - feePct / 100)) : 0
  let level: Judgement['level'] = 'ok'
  let message = `You earn ${money(earn, currency)} a month.`
  if (p <= 0) { level = 'error'; message = 'Enter a price.' }
  else if (p <= cost) { level = 'error'; message = `Your price must be higher than the hosting cost of ${money(cost, currency)}.` }
  else if (earn <= 0) { level = 'warn'; message = `Above hosting cost, but after the ${feePct}% marketplace fee you would lose ${money(-earn, currency)}. At least ${money(breakEven, currency)} covers both.` }
  return { level, message, price_minor: p, cost_minor: cost, fee_minor: fee, earn_minor: earn, break_even_minor: breakEven }
}

const toMinor = (s: string) => { const n = Number(String(s).replace(/[^\d.]/g, '')); return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null }
const toMajor = (m: number | null) => (m == null ? '' : String(m / 100))

export default function SellPage() {
  const { project: projectParam } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof getSellProjects>> | null>(null)
  const [listing, setListing] = useState<Listing | null>(null)
  const [step, setStep] = useState(1)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getSellProjects().then(setOverview).catch((e) => setError(mktError(e).message))
  }, [])

  const openProject = useCallback(async (p: { project: string; owner_user_id: string; listing_id?: number | null }) => {
    try {
      const l = p.listing_id ? await getListing(p.listing_id) : await createListing(p.project, p.owner_user_id)
      setListing(await getListing(l.id))
      setStep(2)
    } catch (e) {
      toast.error(mktError(e).message)
    }
  }, [])

  // Deep link: /marketplace/sell/:project[?owner=]
  useEffect(() => {
    if (!projectParam || !overview || listing) return
    const owner = params.get('owner') || undefined
    const p = overview.projects.find((x) => x.project === projectParam && (!owner || x.owner_user_id === owner))
    if (p) openProject(p)
  }, [projectParam, overview, params, listing, openProject])

  const reload = useCallback(async () => {
    if (listing) setListing(await getListing(listing.id))
  }, [listing])

  if (overview && !overview.enabled) {
    return <MarketplaceShell sell><div className="mkt-wrap" style={{ padding: 40 }}><div className="mkt-empty"><strong>The marketplace is switched off on this Twasta instance.</strong></div></div></MarketplaceShell>
  }
  if (overview && !overview.is_pro) return <MarketplaceShell sell><UpgradeGate planLabel={overview.plan_label} /></MarketplaceShell>

  return (
    <MarketplaceShell sell>
      <div className="mkt-wrap" style={{ padding: '24px 20px 48px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {error && <div className="mkt-banner bad"><div>{error}</div></div>}
        {step <= 5 && <Steps labels={LABELS} current={step} />}
        {listing && listing.status !== 'draft' && step <= 5 && <StatusBanner listing={listing} />}

        {step === 1 && overview && (
          <ChooseApp projects={overview.projects} onPick={openProject} />
        )}
        {listing && step === 2 && <StorePageStep listing={listing} onSaved={setListing} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {listing && step === 3 && <PriceStep listing={listing} onSaved={setListing} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
        {listing && step === 4 && <TrialStep listing={listing} onSaved={setListing} reload={reload} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
        {listing && step === 5 && <ReviewStep listing={listing} onEdit={setStep} onSubmitted={(l) => { setListing(l); setStep(6) }} />}
        {listing && step === 6 && (
          <section className="mkt-panel mkt-col" style={{ maxWidth: 680, margin: '24px auto', alignItems: 'center', textAlign: 'center', gap: 16, padding: 36 }}>
            <h1 style={{ fontSize: 28 }}>{listing.name} is in review</h1>
            <p className="mkt-muted" style={{ margin: 0, maxWidth: 520 }}>We'll email you as soon as it's live — or tell you what to change. You can keep improving your app in the Workbench meanwhile.</p>
            <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', width: '100%' }}>
              <div className="mkt-banner ok" style={{ justifyContent: 'center' }}><div><strong>Sent</strong>Today</div></div>
              <div className="mkt-banner info" style={{ justifyContent: 'center' }}><div><strong>In review</strong>By {fmtDate(listing.review_due_at)}</div></div>
              <div className="mkt-banner plain" style={{ justifyContent: 'center' }}><div><strong>Live</strong>Customers can install</div></div>
            </div>
            <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/marketplace/seller')}>Go to your listings</button>
          </section>
        )}
      </div>
    </MarketplaceShell>
  )
}

function UpgradeGate({ planLabel }: { planLabel: string }) {
  return (
    <div className="mkt-wrap" style={{ padding: '40px 20px' }}>
      <section className="mkt-panel mkt-col" style={{ maxWidth: 860, margin: '0 auto', gap: 22, padding: 36 }}>
        <div className="mkt-col" style={{ alignItems: 'center', textAlign: 'center', gap: 8 }}>
          <h1 style={{ fontSize: 28 }}>Selling apps is part of Twasta Pro</h1>
          <p className="mkt-muted" style={{ margin: 0, maxWidth: 600 }}>You're on the {planLabel} plan. Move to Pro or above to publish your apps in the marketplace, set your own prices and earn from every customer each month.</p>
        </div>
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="mkt-panel mkt-col" style={{ gap: 6 }}><strong>{planLabel}</strong><span className="mkt-faint">Your current plan</span><span className="mkt-muted">Build and use apps in your own organization. Browse and install from the marketplace.</span><span style={{ color: 'var(--color-error)', fontWeight: 600 }}>Can't sell apps</span></div>
          <div className="mkt-panel mkt-panel-accent mkt-col" style={{ gap: 6 }}><strong>Pro</strong><span className="mkt-faint" style={{ color: 'var(--color-accent)' }}>Recommended</span><span className="mkt-muted">Everything in {planLabel}, plus publishing to the marketplace with per-person or per-server pricing.</span><span style={{ color: 'var(--color-success)', fontWeight: 600 }}>Can sell apps</span></div>
          <div className="mkt-panel mkt-col" style={{ gap: 6 }}><strong>SaaS / Enterprise</strong><span className="mkt-faint">For software businesses</span><span className="mkt-muted">Everything in Pro, with more room for many customers.</span><span style={{ color: 'var(--color-success)', fontWeight: 600 }}>Can sell apps</span></div>
        </div>
        <div className="mkt-row" style={{ justifyContent: 'center' }}>
          <Link to="/payments/plans" className="btn btn-primary btn-lg">Upgrade to Pro</Link>
          <Link to="/marketplace" className="btn btn-secondary btn-lg">Back to the marketplace</Link>
        </div>
      </section>
    </div>
  )
}

function StatusBanner({ listing }: { listing: Listing }) {
  const map: Record<string, { cls: string; title: string; text: string }> = {
    in_review: { cls: 'info', title: 'Waiting for review', text: `We'll have looked at it by ${fmtDate(listing.review_due_at)}.` },
    changes_requested: { cls: 'warn', title: 'A few changes are needed', text: listing.review_note || '' },
    live: { cls: 'ok', title: 'Live in the marketplace', text: listing.unreviewed_changes ? 'You have changes to the store page that customers will see after the next review. Prices apply as soon as you save them.' : 'Price changes apply to new customers at once and to existing customers after the notice period.' },
    paused: { cls: 'plain', title: 'Paused', text: 'Hidden from new customers. Existing customers are not affected.' },
    removed: { cls: 'bad', title: 'Removed', text: 'This listing is no longer in the marketplace.' },
  }
  const b = map[listing.status]
  return b ? <div className={`mkt-banner ${b.cls}`}><div><strong>{b.title}</strong>{b.text}</div>{listing.status === 'live' && <Link to={listing.store_url} className="btn btn-secondary" target="_blank">View store page</Link>}</div> : null
}

function ChooseApp({ projects, onPick }: { projects: PublishableProject[]; onPick: (p: PublishableProject) => void }) {
  return (
    <section className="mkt-panel mkt-col" style={{ gap: 18 }}>
      <div className="mkt-col" style={{ gap: 4 }}>
        <h1 style={{ fontSize: 26 }}>Which app do you want to sell?</h1>
        <span className="mkt-muted">You can sell apps where you are the project manager. Each customer gets their own private copy — your app and its design stay yours.</span>
      </div>
      {projects.length === 0 && <div className="mkt-empty"><strong>No apps yet</strong><span className="mkt-muted">Build an app in the Workbench, take it live, then come back to sell it.</span><Link to="/builder" className="btn btn-primary">Build an app</Link></div>}
      <div className="mkt-choices">
        {projects.map((p) => (
          <button key={`${p.owner_user_id}/${p.project}`} type="button" className="mkt-choice mkt-choice-row" onClick={() => onPick(p)} style={{ justifyContent: 'flex-start', gap: 14 }}>
            <AppTile color={p.live ? '#1F56D6' : '#6B7280'} text={p.title.slice(0, 2).toUpperCase()} size={48} />
            <span className="mkt-col" style={{ gap: 4, flex: 1 }}>
              <strong style={{ fontSize: 16 }}>{p.title}</strong>
              <span className="mkt-row" style={{ gap: 8 }}>
                {p.listing_status ? <span className={`mkt-pill ${p.listing_status === 'live' ? 'ok' : 'info'}`}>{statusLabel(p.listing_status)}</span>
                  : p.live ? <span className="mkt-pill ok">Ready to sell</span> : <span className="mkt-pill warn">Not live yet — you can prepare the listing now</span>}
              </span>
            </span>
            <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>{p.listing_id ? 'Open listing' : 'Start listing'}</span>
          </button>
        ))}
      </div>
      <div className="mkt-banner plain"><div><strong>Ready to sell means</strong>the app has gone live in Twasta — its go-live checks pass and the example data is removed.</div></div>
    </section>
  )
}

function statusLabel(s: string): string {
  return ({ draft: 'Draft', in_review: 'In review', changes_requested: 'Changes requested', live: 'Live', paused: 'Paused', removed: 'Removed' } as Record<string, string>)[s] || s
}

function StorePageStep({ listing, onSaved, onBack, onNext }: { listing: Listing; onSaved: (l: Listing) => void; onBack: () => void; onNext: () => void }) {
  const [f, setF] = useState({ name: listing.name || '', tagline: listing.tagline || '', category: listing.category || '', description: listing.description || '', support_email: listing.support_email || '', icon_color: listing.icon_color || '#1F56D6' })
  const [saving, setSaving] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const save = async (next: boolean) => {
    setSaving(true)
    try { onSaved(await updateListing(listing.id, f)); if (next) onNext() } catch (e) { toast.error(mktError(e).message) } finally { setSaving(false) }
  }
  const upload = async (file: File) => {
    try { onSaved(await uploadScreenshot(listing.id, file)); toast.success('Screenshot added') } catch (e) { toast.error(mktError(e).message) }
  }
  const initials = (f.name || 'A').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div className="mkt-split">
      <section className="mkt-main mkt-panel">
        <div className="mkt-col" style={{ gap: 4 }}><h1 style={{ fontSize: 26 }}>Your store page</h1><span className="mkt-muted">What customers see before they install. Write it like you'd explain it to a friend.</span></div>
        <div className="mkt-field"><label htmlFor="n">App name</label><input id="n" value={f.name} maxLength={80} onChange={set('name')} /></div>
        <div className="mkt-field"><label htmlFor="t">One-line description <span className="mkt-faint">{f.tagline.length}/120</span></label><input id="t" value={f.tagline} maxLength={120} onChange={set('tagline')} /></div>
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          <div className="mkt-field"><label htmlFor="c">Category</label><select id="c" value={f.category} onChange={set('category')}><option value="">Choose…</option>{listing.categories.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div className="mkt-field"><label htmlFor="s">Support email for customers</label><input id="s" type="email" value={f.support_email} onChange={set('support_email')} /></div>
          <div className="mkt-field"><label htmlFor="col">Tile colour</label><input id="col" type="color" value={f.icon_color} onChange={set('icon_color')} style={{ height: 44, padding: 4 }} /></div>
        </div>
        <div className="mkt-field"><label htmlFor="d">What can customers do with it?</label><textarea id="d" rows={6} value={f.description} onChange={set('description')} /></div>
        <div className="mkt-col" style={{ gap: 8 }}>
          <strong style={{ fontSize: 14 }}>Screenshots <span className="mkt-faint">— up to 6, PNG / JPEG / WebP, 5 MB each</span></strong>
          <div className="mkt-shots">
            {listing.screenshots.map((s) => (
              <figure key={s.id} style={{ margin: 0, position: 'relative' }}>
                <img src={assetUrl(s.url)} alt={s.caption || 'Screenshot'} />
                <button type="button" className="btn btn-secondary btn-sm" style={{ position: 'absolute', top: 6, right: 6 }} aria-label="Remove screenshot" onClick={async () => onSaved(await deleteScreenshot(listing.id, s.id))}>Remove</button>
              </figure>
            ))}
            {listing.screenshots.length < 6 && (
              <button type="button" className="mkt-choice" style={{ alignItems: 'center', justifyContent: 'center', borderStyle: 'dashed', aspectRatio: '16 / 10' }} onClick={() => fileRef.current?.click()}>
                <strong style={{ color: 'var(--color-accent)' }}>+ Add screenshot</strong>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const file = e.target.files?.[0]; if (file) upload(file); e.target.value = '' }} />
          </div>
        </div>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button>
          <div className="mkt-row"><button type="button" className="btn btn-ghost" disabled={saving} onClick={() => save(false)}>Save</button><button type="button" className="btn btn-primary btn-lg" disabled={saving} onClick={() => save(true)}>Continue to pricing</button></div>
        </div>
      </section>
      <aside className="mkt-aside" aria-label="Preview">
        <span className="mkt-faint" style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>How it looks in the marketplace</span>
        <div className="mkt-card">
          <div className="mkt-row" style={{ gap: 14, flexWrap: 'nowrap' }}><AppTile color={f.icon_color} text={initials} size={52} /><div className="mkt-col" style={{ gap: 0 }}><strong style={{ fontSize: 18 }}>{f.name || 'Your app'}</strong><span className="mkt-muted" style={{ fontSize: 14 }}>by your organization</span></div></div>
          <p>{f.tagline || 'Your one-line description appears here.'}</p>
          {f.category && <span className="mkt-pill" style={{ alignSelf: 'flex-start' }}>{f.category}</span>}
        </div>
      </aside>
    </div>
  )
}

function PriceStep({ listing, onSaved, onBack, onNext }: { listing: Listing; onSaved: (l: Listing) => void; onBack: () => void; onNext: () => void }) {
  const ev = listing.evaluation
  const cur = listing.currency
  const [pricing, setPricing] = useState<Pricing>(listing.pricing)
  const [text, setText] = useState<Record<string, string>>(() => {
    const t: Record<string, string> = { person: toMajor(listing.pricing.person.price_minor) }
    for (const s of listing.server_sizes) t[s.id] = toMajor(listing.pricing.server[s.id]?.price_minor ?? null)
    return t
  })
  const [saving, setSaving] = useState(false)
  const [est, setEst] = useState(10)

  const pj = pricing.person.enabled ? judge(toMinor(text.person), ev.per_person_cost_minor, ev.fee_percent, cur) : null
  const sj = Object.fromEntries(listing.server_sizes.map((s) => [s.id, pricing.server[s.id]?.enabled ? judge(toMinor(text[s.id]), s.hosting_cost_minor, ev.fee_percent, cur) : null]))
  const anyOffer = pricing.person.enabled || Object.values(pricing.server).some((o) => o.enabled)
  const blocked = !anyOffer || pj?.level === 'error' || Object.values(sj).some((j) => j?.level === 'error')

  const save = async () => {
    const next: Pricing = {
      ...pricing,
      person: { ...pricing.person, price_minor: toMinor(text.person) },
      server: Object.fromEntries(listing.server_sizes.map((s) => [s.id, { enabled: !!pricing.server[s.id]?.enabled, price_minor: toMinor(text[s.id]) }])),
    }
    setSaving(true)
    try { onSaved(await updateListing(listing.id, { pricing: next })); onNext() } catch (e) { toast.error(mktError(e).message) } finally { setSaving(false) }
  }

  const gross = (toMinor(text.person) || 0) * est * 10
  const cost = ev.per_person_cost_minor * est * 10
  const fee = Math.round(gross * ev.fee_percent / 100)

  return (
    <div className="mkt-split">
      <section className="mkt-main mkt-panel">
        <div className="mkt-col" style={{ gap: 4 }}>
          <h1 style={{ fontSize: 26 }}>Set your price</h1>
          <span className="mkt-muted">Customers pay you every month. Twasta takes the hosting cost and a {ev.fee_percent}% marketplace fee; the rest is yours. <strong style={{ color: 'var(--color-text)' }}>Your price must be higher than the hosting cost.</strong></span>
        </div>
        <div className="mkt-col" style={{ gap: 8 }}>
          <strong>How should customers pay? <span className="mkt-muted" style={{ fontWeight: 400 }}>Pick one or both.</span></strong>
          <div className="mkt-choices" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button type="button" role="checkbox" aria-checked={pricing.person.enabled} className={`mkt-choice${pricing.person.enabled ? ' on' : ''}`} onClick={() => setPricing({ ...pricing, person: { ...pricing.person, enabled: !pricing.person.enabled } })}><strong>Per person</strong><span className="mkt-muted" style={{ fontSize: 14 }}>A monthly price for each person using the app.</span></button>
            <button type="button" role="checkbox" aria-checked={Object.values(pricing.server).some((o) => o.enabled)} className={`mkt-choice${Object.values(pricing.server).some((o) => o.enabled) ? ' on' : ''}`}
              onClick={() => { const on = !Object.values(pricing.server).some((o) => o.enabled); setPricing({ ...pricing, server: Object.fromEntries(listing.server_sizes.map((s) => [s.id, { enabled: on, price_minor: pricing.server[s.id]?.price_minor ?? null }])) }) }}>
              <strong>Per server</strong><span className="mkt-muted" style={{ fontSize: 14 }}>A flat monthly price for a whole team, by size.</span></button>
          </div>
        </div>

        {pricing.person.enabled && pj && (
          <div className="mkt-panel mkt-col" style={{ padding: 18 }}>
            <div className="mkt-row" style={{ alignItems: 'flex-end', gap: 18 }}>
              <div className="mkt-field">
                <label htmlFor="pp">Price per person, per month</label>
                <div className={`mkt-money ${pj.level === 'error' ? 'bad' : pj.level === 'warn' ? 'warn' : ''}`}><span>{cur === 'INR' ? '₹' : '$'}</span><input id="pp" inputMode="decimal" value={text.person} onChange={(e) => setText({ ...text, person: e.target.value })} aria-describedby="pp-msg" /></div>
              </div>
              <div className="mkt-col" style={{ gap: 0, background: 'var(--color-raised, var(--color-bg))', borderRadius: 12, padding: '10px 14px' }}>
                <span className="mkt-faint">Twasta hosting cost</span><strong>{money(ev.per_person_cost_minor, cur)} per person</strong><span className="mkt-faint">Servers, management and support included</span>
              </div>
            </div>
            <div id="pp-msg" className={`mkt-msg ${pj.level}`} role="status">{pj.message}</div>
            {pj.price_minor > 0 && (
              <div className="mkt-col" style={{ gap: 6 }}>
                <span className="mkt-faint">Where each person's {money(pj.price_minor, cur)} goes</span>
                <div className="mkt-stack" role="img" aria-label={`Hosting ${money(pj.cost_minor, cur)}, fee ${money(pj.fee_minor, cur)}, you ${money(pj.earn_minor, cur)}`}>
                  <span style={{ flex: pj.cost_minor, background: '#3A404C' }} /><span style={{ flex: Math.max(pj.fee_minor, 1), background: '#C9CED6' }} /><span style={{ flex: Math.max(pj.earn_minor, 0), background: 'var(--color-success)' }} />
                </div>
                <span className="mkt-faint">Hosting {money(pj.cost_minor, cur)} · Fee {money(pj.fee_minor, cur)} · You {money(pj.earn_minor, cur)}</span>
              </div>
            )}
          </div>
        )}

        {Object.values(pricing.server).some((o) => o.enabled) && (
          <div className="mkt-panel" style={{ padding: 18 }}>
            <strong>Price per server, per month</strong>
            <div className="mkt-scroll">
              <table className="mkt-table" style={{ minWidth: 620 }}>
                <thead><tr><th scope="col">Offer</th><th scope="col">Size</th><th scope="col" className="num">Hosting cost</th><th scope="col">Your price</th><th scope="col" className="num">You earn</th></tr></thead>
                <tbody>
                  {listing.server_sizes.map((s) => {
                    const on = !!pricing.server[s.id]?.enabled
                    const j = sj[s.id]
                    return (
                      <tr key={s.id}>
                        <td><input type="checkbox" checked={on} aria-label={`Offer ${s.name}`} onChange={() => setPricing({ ...pricing, server: { ...pricing.server, [s.id]: { enabled: !on, price_minor: pricing.server[s.id]?.price_minor ?? null } } })} style={{ width: 20, height: 20 }} /></td>
                        <td><strong>{s.name}</strong><br /><span className="mkt-faint">Up to {s.max_people} people</span></td>
                        <td className="num">{money(s.hosting_cost_minor, cur)}</td>
                        <td>
                          <div className={`mkt-money ${on && j?.level === 'error' ? 'bad' : on && j?.level === 'warn' ? 'warn' : ''}`} style={{ maxWidth: 170, opacity: on ? 1 : 0.5 }}><span>{cur === 'INR' ? '₹' : '$'}</span><input disabled={!on} inputMode="decimal" aria-label={`Price for ${s.name}`} value={text[s.id] ?? ''} onChange={(e) => setText({ ...text, [s.id]: e.target.value })} style={{ padding: '9px 8px' }} /></div>
                          {on && j && j.level !== 'ok' && <div style={{ fontSize: 13, marginTop: 4, color: j.level === 'error' ? 'var(--color-error)' : 'var(--color-warning)', maxWidth: 260 }}>{j.message}</div>}
                        </td>
                        <td className="num" style={{ fontWeight: 700, color: !on ? 'var(--color-text-faint)' : (j && j.earn_minor > 0) ? 'var(--color-success)' : 'var(--color-error)' }}>{on && j ? money(j.earn_minor, cur) : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button>
          <div className="mkt-row">
            {blocked && <span style={{ color: 'var(--color-error)', fontWeight: 600, fontSize: 14 }}>{anyOffer ? 'Fix the prices in red to continue' : 'Offer at least one way to pay'}</span>}
            <button type="button" className="btn btn-primary btn-lg" disabled={blocked || saving} onClick={save}>Continue</button>
          </div>
        </div>
      </section>

      {pricing.person.enabled && (
        <aside className="mkt-aside mkt-dark mkt-col" aria-label="Earnings estimate">
          <span className="mkt-muted" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>What you could earn</span>
          <span className="mkt-muted">If <strong style={{ color: '#fff' }}>{est} businesses</strong> each have <strong style={{ color: '#fff' }}>10 people</strong> on the per-person plan:</span>
          <strong style={{ fontSize: 32 }}>{money(gross - cost - fee, cur)}<span className="mkt-muted" style={{ fontSize: 15, fontWeight: 400 }}> / month</span></strong>
          <div className="mkt-row">
            <button type="button" className="btn" style={{ background: 'transparent', color: '#fff', border: '1px solid #5A6170' }} aria-label="Fewer businesses" onClick={() => setEst(Math.max(1, est - 1))}>−</button>
            <button type="button" className="btn" style={{ background: 'transparent', color: '#fff', border: '1px solid #5A6170' }} aria-label="More businesses" onClick={() => setEst(est + 1)}>+</button>
          </div>
          <div className="mkt-col" style={{ gap: 6, borderTop: '1px solid #3A404C', paddingTop: 12, fontSize: 14 }}>
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Customers pay</span><span>{money(gross, cur)}</span></div>
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Hosting</span><span>− {money(cost, cur)}</span></div>
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Marketplace fee ({ev.fee_percent}%)</span><span>− {money(fee, cur)}</span></div>
          </div>
          <span className="mkt-muted" style={{ fontSize: 13 }}>Your own organization always uses your app for free.</span>
        </aside>
      )}
    </div>
  )
}

function TrialStep({ listing, onSaved, reload, onBack, onNext }: { listing: Listing; onSaved: (l: Listing) => void; reload: () => Promise<void>; onBack: () => void; onNext: () => void }) {
  const rules = listing.trial_rules
  const [days, setDays] = useState(listing.pricing.trial.days)
  const [people, setPeople] = useState(listing.pricing.trial.people || rules.people_options[rules.people_options.length - 1])
  const [trials, setTrials] = useState(rules.topup_trial_options.includes(10) ? 10 : rules.topup_trial_options[0])
  const [from, setFrom] = useState<'checkout' | 'wallet'>('checkout')
  const [busy, setBusy] = useState(false)
  const { checkout, busy: paying } = useRazorpayCheckout()
  const cur = listing.currency
  const daily = listing.evaluation.per_person_cost_minor / 30
  const perTrial = Math.ceil(people * days * daily)
  const budget = listing.trial_budget
  const covered = budget.available_minor >= perTrial && perTrial > 0
  const amount = trials * perTrial

  const saveTrial = async () => {
    const l = await updateListing(listing.id, { pricing: { ...listing.pricing, trial: { days, people: days ? people : 0 } } })
    onSaved(l)
    return l
  }

  const payAndContinue = async () => {
    setBusy(true)
    try {
      await saveTrial()
      if (days > 0 && !covered) {
        if (from === 'wallet') {
          onSaved(await budgetFromWallet(listing.id, trials))
        } else {
          const r = await checkout({ purpose: 'trial_budget_topup', description: 'Free-trial budget', precreate: () => budgetCheckout(listing.id, trials) })
          if (r.cancelled) { setBusy(false); return }
          if (!r.ok) { toast.error(r.error || 'The payment did not go through.'); setBusy(false); return }
          toast.success('Trial budget topped up')
          await reload()
        }
      }
      onNext()
    } catch (e) {
      toast.error(mktError(e).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mkt-split">
      <section className="mkt-main mkt-panel">
        <div className="mkt-col" style={{ gap: 4 }}>
          <h1 style={{ fontSize: 26 }}>Free trial</h1>
          <span className="mkt-muted">A free trial helps customers decide. While they try your app nobody is paying for its hosting, so you fund trials from a trial budget you top up in advance.</span>
        </div>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <strong>How long can customers try it free?</strong>
          <div className="mkt-chips" role="radiogroup" aria-label="Trial length">
            {[0, ...rules.allowed_days.filter((d) => d <= rules.max_days)].map((d) => (
              <button key={d} type="button" role="radio" aria-checked={days === d} className={`mkt-chip${days === d ? ' on' : ''}`} onClick={() => setDays(d)}>{d ? `${d} days` : 'No trial'}</button>
            ))}
          </div>
        </div>
        {days > 0 && (
          <>
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
              <strong>How many people in one trial?</strong>
              <div className="mkt-chips" role="radiogroup" aria-label="Trial size">
                {rules.people_options.filter((n) => n <= rules.max_people).map((n) => (
                  <button key={n} type="button" role="radio" aria-checked={people === n} className={`mkt-chip${people === n ? ' on' : ''}`} onClick={() => setPeople(n)}>Up to {n}</button>
                ))}
              </div>
            </div>
            <div className="mkt-banner plain">
              <div><strong>Each trial costs you at most {money(perTrial, cur)}</strong>{people} people × {days} days × {money(Math.round(daily), cur)} a person a day (the {money(listing.evaluation.per_person_cost_minor, cur)} monthly hosting ÷ 30). You pay only for the days actually used.</div>
            </div>
            {covered ? (
              <div className="mkt-msg ok">Your trial budget has {money(budget.available_minor, cur)} — enough for {Math.floor(budget.available_minor / perTrial)} trials.</div>
            ) : (
              <>
                <div className="mkt-col" style={{ gap: 8 }}>
                  <strong>Top up your trial budget</strong>
                  <div className="mkt-choices" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }} role="radiogroup" aria-label="Trial budget">
                    {rules.topup_trial_options.map((n) => (
                      <button key={n} type="button" role="radio" aria-checked={trials === n} className={`mkt-choice${trials === n ? ' on' : ''}`} onClick={() => setTrials(n)}>
                        <strong style={{ fontSize: 19 }}>{money(n * perTrial, cur)}</strong><span className="mkt-muted" style={{ fontSize: 14 }}>{n} trials</span>
                      </button>
                    ))}
                  </div>
                  <span className="mkt-faint">The smallest top-up covers {rules.min_topup_trials} trials.</span>
                </div>
                <div className="mkt-col" style={{ gap: 8 }}>
                  <strong>Pay with</strong>
                  <div className="mkt-choices" style={{ gridTemplateColumns: '1fr 1fr' }} role="radiogroup" aria-label="Pay with">
                    <button type="button" role="radio" aria-checked={from === 'checkout'} className={`mkt-choice${from === 'checkout' ? ' on' : ''}`} onClick={() => setFrom('checkout')}><strong>UPI or card</strong><span className="mkt-muted" style={{ fontSize: 14 }}>GST is added at payment</span></button>
                    <button type="button" role="radio" aria-checked={from === 'wallet'} className={`mkt-choice${from === 'wallet' ? ' on' : ''}`} onClick={() => setFrom('wallet')}><strong>App wallet</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Your marketplace earnings</span></button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button>
          <button type="button" className="btn btn-primary btn-lg" disabled={busy || paying} onClick={payAndContinue}>
            {days === 0 ? 'Continue without a trial' : covered ? 'Continue' : `Pay ${money(amount, cur)} and continue`}
          </button>
        </div>
      </section>
      <aside className="mkt-aside mkt-panel" aria-label="How the trial budget works">
        <strong style={{ fontSize: 17 }}>How your trial budget works</strong>
        <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          <li>When a customer starts a trial, its full cost is set aside from your budget.</li>
          <li>Each day, only what they actually use is taken.</li>
          <li>When the trial ends — bought or not — whatever wasn't used goes back to your budget.</li>
          <li>We email you when it covers {rules.low_budget_trials} trials or fewer.</li>
          <li><strong>If it can't cover one more trial, free trials stop.</strong> New customers see no trial and pay from day one. Trials already running always finish.</li>
          <li>Turn trials off any time; the unused budget moves to your app wallet.</li>
        </ol>
      </aside>
    </div>
  )
}

function ReviewStep({ listing, onEdit, onSubmitted }: { listing: Listing; onEdit: (s: number) => void; onSubmitted: (l: Listing) => void }) {
  const [agree, setAgree] = useState(false)
  const [busy, setBusy] = useState(false)
  const cur = listing.currency
  const ev = listing.evaluation
  const offers = useMemo(() => {
    const out: { name: string; j: Judgement }[] = []
    if (ev.person) out.push({ name: 'Per person / month', j: ev.person })
    for (const s of listing.server_sizes) if (ev.server[s.id]) out.push({ name: `${s.name} server / month`, j: ev.server[s.id] })
    return out
  }, [ev, listing.server_sizes])
  const canSubmit = listing.problems.length === 0 && listing.status !== 'in_review' && listing.status !== 'removed'
  const submit = async () => {
    setBusy(true)
    try { onSubmitted(await submitListing(listing.id)) } catch (e) { toast.error(mktError(e).message) } finally { setBusy(false) }
  }
  const t = listing.pricing.trial
  return (
    <div className="mkt-split">
      <section className="mkt-main mkt-panel">
        <div className="mkt-col" style={{ gap: 4 }}><h1 style={{ fontSize: 26 }}>Check and send for review</h1><span className="mkt-muted">Here's exactly what customers will see and get.</span></div>
        {listing.problems.length > 0 && (
          <div className="mkt-banner warn"><div><strong>Before you can send it</strong><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{listing.problems.map((p) => <li key={p}>{p}</li>)}</ul></div></div>
        )}
        <div className="mkt-col" style={{ gap: 8 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}><h2>Prices</h2><button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(3)}>Edit</button></div>
          <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            {offers.map((o) => (<div key={o.name} className="mkt-panel" style={{ padding: 14 }}><span className="mkt-muted" style={{ fontSize: 14 }}>{o.name}</span><br /><strong style={{ fontSize: 20 }}>{money(o.j.price_minor, cur)}</strong><br /><span style={{ fontSize: 13, color: o.j.earn_minor > 0 ? 'var(--color-success)' : 'var(--color-error)' }}>You earn {money(o.j.earn_minor, cur)}</span></div>))}
          </div>
        </div>
        <div className="mkt-col" style={{ gap: 8 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}><h2>Free trial</h2><button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(4)}>Edit</button></div>
          <div className="mkt-panel mkt-row" style={{ padding: 14, justifyContent: 'space-between' }}>
            <span>{t.days ? `${t.days} days free for up to ${t.people} people, then customers pay monthly.` : 'No free trial — customers pay from day one.'}</span>
            {t.days > 0 && <span className={`mkt-pill ${listing.trial_budget.trials_left > 0 ? 'ok' : 'bad'}`}>Budget {money(listing.trial_budget.available_minor, cur)} · {listing.trial_budget.trials_left} trials</span>}
          </div>
        </div>
        <div className="mkt-col" style={{ gap: 8 }}>
          <h2>What every customer gets</h2>
          <span className="mkt-muted" style={{ fontSize: 14 }}>These limits come with Twasta hosting and are shown on your store page. They keep every customer's app fast and fair.</span>
          <div className="mkt-scroll">
            <table className="mkt-table" style={{ minWidth: 560 }}>
              <thead><tr><th scope="col">Plan</th><th scope="col">People</th><th scope="col">Room for records</th><th scope="col">Files & photos</th><th scope="col" className="num">Hosting cost</th></tr></thead>
              <tbody>
                {pricingRowPerson(listing)}
                {listing.server_sizes.map((s) => (
                  <tr key={s.id}><td>{s.name} server</td><td>Up to {s.max_people}</td><td>{s.storage_gb} GB</td><td>{s.files_gb} GB</td><td className="num">{money(s.hosting_cost_minor, cur)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <label className="mkt-row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'nowrap', cursor: 'pointer' }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ width: 20, height: 20, marginTop: 2 }} />
          <span>I agree to the publisher terms, and I'll support customers at {listing.support_email || 'my support email'}.</span>
        </label>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={() => onEdit(4)}>Back</button>
          <button type="button" className="btn btn-primary btn-lg" disabled={!agree || !canSubmit || busy} onClick={submit}>Send for review</button>
        </div>
      </section>
      <aside className="mkt-aside mkt-panel">
        <strong style={{ fontSize: 17 }}>What happens next</strong>
        <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          <li>Twasta checks your listing within {listing.is_published ? listing.review_days.update_review_business_days : listing.review_days.first_review_business_days} business days.</li>
          <li>We email you when it's live — or what to fix.</li>
          <li>Customers install it; each gets a private copy and database.</li>
          <li>Each month they pay, and your earnings land in your app wallet.</li>
          <li>Changing prices later? Existing customers keep their price for {listing.notice_days} days after we tell them.</li>
        </ol>
      </aside>
    </div>
  )
}

function pricingRowPerson(listing: Listing) {
  if (!listing.pricing.person.enabled) return null
  return <tr><td>Per person</td><td>As many as they pay for</td><td>Grows with people</td><td>Grows with people</td><td className="num">{money(listing.evaluation.per_person_cost_minor, listing.currency)} a person</td></tr>
}
